import React from "react";
import { useTranslation } from "react-i18next";
import { HeroWarm } from "@/components/sections/HeroWarm";
import { FeaturedServices } from "@/components/sections/FeaturedServices";
import { WhyChooseUs } from "@/components/sections/WhyChooseUs";
import { StatsCounters } from "@/components/sections/StatsCounters";
import { ReferralRequiredNotice } from "@/components/sections/ReferralRequiredNotice";
import { SEO } from "@/lib/seo";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Video, Globe2, Clock } from "lucide-react";

export default function HomePage() {
    const { t } = useTranslation();
    const testimonials = [
        { initials: "J.M.", quote: t("home.testimonial1") },
        { initials: "A.R.", quote: t("home.testimonial2") },
        { initials: "S.T.", quote: t("home.testimonial3") },
    ];
    return (
        <>
            <SEO title={t("home.seoTitle")} description={t("home.seoDescription")} />
            <HeroWarm />

            <FeaturedServices />

            {/* Virtual consultations highlight */}
            <section data-testid="home-virtual-highlight" className="container-page section-y">
                <div
                    className="relative overflow-hidden rounded-3xl border border-[color:var(--border-default)] p-8 sm:p-12 grain"
                    style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.12) 0%, rgba(15,39,71,0.06) 100%)" }}
                >
                    <div className="grid gap-8 lg:grid-cols-12 items-center relative">
                        <div className="lg:col-span-7">
                            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-[color:var(--brand-teal-2)] border border-[color:var(--brand-teal-2)]/25">
                                <Video className="h-3.5 w-3.5" /> {t("home.virtualHighlightChip")}
                            </span>
                            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-[color:var(--brand-navy)]">
                                {t("home.virtualHighlightTitle")}
                            </h2>
                            <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed max-w-2xl">
                                {t("home.virtualHighlightBody")}
                            </p>
                            <div className="mt-5 grid gap-3 sm:grid-cols-3 max-w-2xl">
                                {[
                                    { icon: Globe2, text: t("home.virtualBenefit1") },
                                    { icon: Clock, text: t("home.virtualBenefit2") },
                                    { icon: ShieldCheck, text: t("home.virtualBenefit3") },
                                ].map(({ icon: Icon, text }) => (
                                    <div key={text} className="flex items-center gap-2 rounded-xl bg-white border border-[color:var(--border-default)] px-3 py-2">
                                        <Icon className="h-4 w-4 text-[color:var(--brand-teal-2)]" />
                                        <span className="text-xs font-medium text-[color:var(--brand-navy)]">{text}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                <Button asChild size="lg" data-testid="home-virtual-cta-primary">
                                    <Link to="/virtual-consultations" className="gap-1.5">
                                        <Video className="h-4 w-4" />
                                        {t("home.howVirtualVisitsWork")}
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg" data-testid="home-virtual-cta-secondary">
                                    <Link to="/referrals" className="gap-1.5">
                                        {t("cta.referralProcess")}
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </Button>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { n: "1", tx: t("home.stepFamilyRefers") },
                                    { n: "2", tx: t("home.stepFaxTo") },
                                    { n: "3", tx: t("home.stepWeSchedule") },
                                    { n: "4", tx: t("home.stepWrittenPlan") },
                                ].map((s) => (
                                    <div key={s.n} className="rounded-2xl bg-white border border-[color:var(--border-default)] p-4">
                                        <span
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white text-sm font-semibold"
                                            style={{ background: "var(--brand-teal)" }}
                                        >
                                            {s.n}
                                        </span>
                                        <p className="mt-3 text-sm font-semibold text-[color:var(--brand-navy)] leading-tight">{s.tx}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="section-y">
                <ReferralRequiredNotice />
            </div>

            <StatsCounters />

            <WhyChooseUs />

            <section data-testid="testimonials" className="container-page section-y">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("home.testimonialsChip")}</span>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-[color:var(--brand-navy)]">
                        {t("home.testimonialsTitle")}
                    </h2>
                    <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed">
                        {t("home.testimonialsIntro")}
                    </p>
                </div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {testimonials.map((testimonial) => (
                        <figure key={testimonial.initials} className="card-soft p-6">
                            <blockquote className="text-[color:var(--text-primary)] leading-relaxed">
                                “{testimonial.quote}”
                            </blockquote>
                            <figcaption className="mt-4 flex items-center gap-3">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)] font-semibold text-sm">
                                    {testimonial.initials}
                                </span>
                                <span className="text-xs text-[color:var(--text-soft)]">{t("home.patientReflection")}</span>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </section>

            <section className="container-page section-y">
                <div className="grid gap-6 lg:grid-cols-2 items-center">
                    <div>
                        <span className="chip-teal">{t("home.resourcesChip")}</span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-[color:var(--brand-navy)]">
                            {t("home.resourcesTitle")}
                        </h2>
                        <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed">
                            {t("home.resourcesIntro")}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link
                                to="/resources"
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-teal-2)] hover:gap-2 transition-[gap]"
                            >
                                {t("nav.patientResourcesHub")}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/faq"
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-navy)] hover:gap-2 transition-[gap]"
                            >
                                {t("home.readFaq")}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {[
                            { title: t("home.resourceCardPrepColo"), to: "/resources/preparing-for-colonoscopy" },
                            { title: t("home.resourceCardPrepGastro"), to: "/resources/preparing-for-gastroscopy" },
                            { title: t("home.resourceCardMedication"), to: "/resources/medication-guidelines" },
                            { title: t("home.resourceCardProcedureDay"), to: "/resources/procedure-day-expectations" },
                        ].map((r) => (
                            <Link key={r.to} to={r.to} className="card-soft p-4 group">
                                <div className="text-sm font-semibold text-[color:var(--brand-navy)]">{r.title}</div>
                                <div className="mt-1 text-xs text-[color:var(--brand-teal-2)] inline-flex items-center gap-1 group-hover:gap-1.5 transition-[gap]">
                                    {t("cta.readGuide")} <ArrowRight className="h-3.5 w-3.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
